/**
 * Audit: qui a fait quoi, et quand (SUPER_ADMIN; ADMIN_SOCIETE pour sa société).
 * Deux écrans, un seul composant (prop `vue`):
 *  - « securite » (/audit): alertes de sécurité, analyses, activité d'un utilisateur;
 *  - « journal » (/journal): filtres, tableau des actions, détail, export pour Excel.
 * Chaque écran ne charge que ses propres données.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '../hooks/use-debounced-value';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Download, FileClock, User as UserIcon, Clock, Eye, RefreshCw, X, ShieldAlert } from 'lucide-react';
import { adminAPI, api, organizationsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { PageHeader } from '../components/ui/page-header';
import { EmptyState } from '../components/ui/empty-state';
import { SearchableSelect } from '../components/ui/searchable-select';
import { organizationOption } from '../lib/selectOptions';
import { libelle, nomLisible, optionsLibelles } from '../lib/libelles';
import { messageErreur } from '../lib/erreurs';
import { formatDateHeure, formatNombre } from '../lib/formats';
import { usePaginationServeur } from '../hooks/usePagination';
import { useDerniereDemande } from '../hooks/useDerniereDemande';
import { BarrePagination } from '../components/ui/pagination-bar';
import {
  AnalyticsCard,
  AnomalyCard,
  Badge,
  DetailAction,
  Field,
  actionColor,
  fmtDate,
  useActiviteUtilisateur,
} from '../components/audit/AuditParts';

const PRESETS = {
  critical: {
    label: 'Actions sensibles',
    filters: {
      action: 'LOGIN_FAILED,USER_DELETED,ORG_SUSPENDED,LICENSE_REVOKED',
    },
  },
};

// Actions proposées dans le filtre: un seul choix par libellé.
const OPTIONS_ACTION = (() => {
  const vues = new Set();
  return optionsLibelles('auditAction').filter((option) => {
    if (vues.has(option.label)) return false;
    vues.add(option.label);
    return true;
  });
})();
const OPTIONS_ELEMENT = optionsLibelles('auditEntity');

const FILTRES_VIDES = {
  user_id: '',
  organization_id: '',
  action: '',
  entity_type: '',
  date_from: '',
  date_to: '',
  search: '',
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* ------------------------------------------------------------------ */
/* Alertes de sécurité                                                 */
/* ------------------------------------------------------------------ */

function VueSecurite() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [ouvrirActivite, fenetreActivite] = useActiviteUtilisateur();

  const charger = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    try {
      const [a, n] = await Promise.allSettled([
        adminAPI.getAuditAnalytics(30),
        adminAPI.getAnomalies(24),
      ]);
      if (a.status === 'fulfilled') setAnalytics(a.value);
      if (n.status === 'fulfilled') setAnomalies(n.value);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    charger();
  }, [charger]);

  const ouvrirDepuisAlerte = (item) => ouvrirActivite(item._id, item.name);

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Sécurité"
        title="Audit sécurité"
        description="Les alertes de sécurité et les analyses de l’activité."
        actions={
          <>
            {isSuperAdmin && (
              <button onClick={charger} className="btn-secondary px-3">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Rafraîchir
              </button>
            )}
            <Link to="/journal" className="btn-secondary px-3">
              <FileClock className="h-4 w-4" aria-hidden="true" /> Voir le journal complet
            </Link>
          </>
        }
      />

      {!isSuperAdmin && (
        <EmptyState
          variant="empty"
          icon={ShieldAlert}
          title="Alertes réservées à la plateforme"
          description="Les alertes de sécurité et les analyses sont visibles par l’administrateur de la plateforme. Le journal des actions de votre société reste disponible."
          actionLabel="Voir le journal complet"
          onAction={() => navigate('/journal')}
        />
      )}

      {/* Alertes */}
      {isSuperAdmin && anomalies && (
        <div className="grid gap-4 lg:grid-cols-3">
          <AnomalyCard
            title="Échecs de connexion"
            subtitle="Plus de 5 échecs sur 24 h"
            items={anomalies.failed_logins || []}
            color="rose"
            onItemClick={ouvrirDepuisAlerte}
          />
          <AnomalyCard
            title="Activité hors heures"
            subtitle="Entre 22 h et 6 h (heure UTC)"
            items={anomalies.after_hours_activity || []}
            color="amber"
            onItemClick={ouvrirDepuisAlerte}
          />
          <AnomalyCard
            title="Suppressions massives"
            subtitle="Plus de 10 suppressions par utilisateur"
            items={anomalies.mass_deletions || []}
            color="rose"
            onItemClick={ouvrirDepuisAlerte}
          />
        </div>
      )}

      {/* Statistiques */}
      {isSuperAdmin && analytics && (
        <div className="grid gap-4 lg:grid-cols-3">
          <AnalyticsCard title="Utilisateurs les plus actifs" items={analytics.top_users} color="sky" labelKey="name" />
          <AnalyticsCard title="Actions les plus fréquentes" items={analytics.top_actions} color="amber" domaine="auditAction" />
          <AnalyticsCard title="Éléments les plus modifiés" items={analytics.top_entities} color="violet" domaine="auditEntity" />
        </div>
      )}

      {fenetreActivite}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Journal des actions                                                 */
/* ------------------------------------------------------------------ */

function VueJournal() {
  const { isSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const preset = searchParams.get('preset');
  const presetUserId = searchParams.get('user_id');

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [organisations, setOrganisations] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [ouvrirActivite, fenetreActivite] = useActiviteUtilisateur();

  const [filters, setFilters] = useState({
    ...FILTRES_VIDES,
    user_id: presetUserId || '',
    action: preset ? PRESETS[preset]?.filters?.action || '' : '',
  });

  // La recherche partait en requête à chaque touche. Le reste des filtres est
  // choisi dans des listes, une frappe n'a donc que ce champ à attendre.
  const debouncedSearch = useDebouncedValue(filters.search);
  const queryFilters = useMemo(
    () => ({ ...filters, search: debouncedSearch }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filters.user_id,
      filters.organization_id,
      filters.action,
      filters.entity_type,
      filters.date_from,
      filters.date_to,
      debouncedSearch,
    ],
  );

  // Pagination côté serveur: l'API reçoit skip/limit, la page vit dans l'adresse.
  const pagination = usePaginationServeur(total, {
    reinitialiser: JSON.stringify(queryFilters),
    taillePrefere: 50,
  });
  const { skip, limit } = pagination;

  const nouvelleDemande = useDerniereDemande();
  const loadLogs = useCallback(async () => {
    const perimee = nouvelleDemande();
    setLoading(true);
    try {
      const params = { skip, limit };
      Object.entries(queryFilters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });

      let data;
      if (isSuperAdmin) {
        data = await adminAPI.getAuditLogs(params);
      } else {
        // `/audit` rend `{ data: [...], total }`: on garde l'enveloppe entière, sinon le total
        // devient la taille de la page et la pagination s'arrête à la première.
        const res = await api.get('/audit', { params });
        data = res.data;
      }
      if (perimee()) return;
      // Normalize shape: /admin/audit-logs returns {items,total,...}, /audit returns {data,total}
      if (Array.isArray(data)) {
        setLogs(data);
        setTotal(data.length);
      } else if (data?.items) {
        setLogs(data.items);
        setTotal(data.total || 0);
      } else if (data?.data) {
        setLogs(data.data);
        setTotal(data.total || 0);
      } else {
        setLogs([]);
        setTotal(0);
      }
    } catch (e) {
      if (perimee()) return;
      console.error(e);
      toast.error(messageErreur(e, 'Impossible de charger le journal des actions'));
    } finally {
      if (!perimee()) setLoading(false);
    }
  }, [queryFilters, skip, limit, isSuperAdmin, nouvelleDemande]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let actif = true;
    organizationsAPI
      .getEvery()
      .then((liste) => {
        if (actif) setOrganisations(Array.isArray(liste) ? liste : []);
      })
      .catch(() => {});
    return () => {
      actif = false;
    };
  }, [isSuperAdmin]);

  const optionsOrganisations = useMemo(
    () => organisations.map(organizationOption),
    [organisations],
  );

  const updateFilter = (k, v) => {
    setFilters((f) => ({ ...f, [k]: v }));
  };

  const resetFilters = () => {
    setFilters({ ...FILTRES_VIDES });
  };

  // Le journal se remplit tout seul: la seule raison pour laquelle il peut
  // être vide alors que la société travaille, c'est un filtre trop étroit.
  const hasActiveFilters = Object.values(filters).some((value) => value !== '');

  // Export pour Excel: colonnes nommées en français, valeurs lisibles, séparateur « ; ».
  const exportLogs = async () => {
    try {
      const colonnes = ['Date et heure', 'Action', 'Élément', 'Utilisateur', 'Organisation'];
      const cellule = (valeur) => `"${String(valeur ?? '').replace(/"/g, '""')}"`;
      const lines = [colonnes.map(cellule).join(';')];
      logs.forEach((l) => {
        lines.push(
          [
            formatDateHeure(l.timestamp),
            libelle('auditAction', l.action),
            libelle('auditEntity', l.entity_type),
            nomLisible(l.changed_by_name, 'Système'),
            nomLisible(l.organization_name, '—'),
          ]
            .map(cellule)
            .join(';'),
        );
      });
      const blob = new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
      downloadBlob(blob, `journal-des-actions-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success('Export téléchargé');
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible d’exporter le journal'));
    }
  };

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Plateforme"
        title="Journal des actions"
        description="Qui a fait quoi, et quand: toutes les actions enregistrées."
        actions={
          <>
            <button onClick={() => loadLogs()} className="btn-secondary px-3">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Rafraîchir
            </button>
            <button onClick={() => exportLogs()} className="btn-secondary px-3">
              <Download className="h-4 w-4" aria-hidden="true" /> Exporter (Excel)
            </button>
            <Link to="/audit" className="btn-secondary px-3">
              <ShieldAlert className="h-4 w-4" aria-hidden="true" /> Voir les alertes de sécurité
            </Link>
          </>
        }
      />

      {/* Filtres */}
      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Recherche">
            <input
              type="text"
              placeholder="Nom d’une personne, action, élément…"
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              className="w-full rounded-md border border-border px-3 py-2 text-sm"
            />
          </Field>
          {filters.user_id && (
            <Field label="Utilisateur">
              <button
                type="button"
                onClick={() => updateFilter('user_id', '')}
                className="inline-flex h-9 items-center gap-1 rounded-md border border-border bg-info/10 px-3 text-sm text-info hover:bg-info/15"
              >
                Une seule personne <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </Field>
          )}
          {isSuperAdmin && (
            <Field label="Organisation">
              <SearchableSelect
                className="h-9 w-56"
                aria-label="Organisation"
                placeholder="Toutes les organisations"
                searchPlaceholder="Rechercher une organisation…"
                emptyMessage="Aucune organisation trouvée"
                value={filters.organization_id}
                onValueChange={(valeur) => updateFilter('organization_id', valeur === filters.organization_id ? '' : valeur)}
                options={optionsOrganisations}
              />
            </Field>
          )}
          <Field label="Action">
            <select
              value={filters.action}
              onChange={(e) => updateFilter('action', e.target.value)}
              className="w-56 rounded-md border border-border px-3 py-2 text-sm"
            >
              <option value="">Toutes les actions</option>
              {filters.action && !OPTIONS_ACTION.some((o) => o.value === filters.action) && (
                <option value={filters.action}>{PRESETS.critical.label}</option>
              )}
              {OPTIONS_ACTION.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Élément">
            <select
              value={filters.entity_type}
              onChange={(e) => updateFilter('entity_type', e.target.value)}
              className="w-48 rounded-md border border-border px-3 py-2 text-sm"
            >
              <option value="">Tous les éléments</option>
              {OPTIONS_ELEMENT.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Du">
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => updateFilter('date_from', e.target.value)}
              className="rounded-md border border-border px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Au">
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => updateFilter('date_to', e.target.value)}
              className="rounded-md border border-border px-3 py-2 text-sm"
            />
          </Field>
          <button
            onClick={resetFilters}
            className="inline-flex h-9 items-center gap-1 rounded-md border border-border bg-muted/50 px-3 text-sm text-muted-foreground hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" /> Effacer les filtres
          </button>
        </div>
      </div>

      {/* Tableau des actions */}
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b border-border/60 p-4">
          <div className="text-sm font-semibold text-muted-foreground">
            {formatNombre(total)} actions enregistrées {loading ? '(chargement…)' : ''}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left readout-label">
              <tr>
                <th className="px-4 py-3">Date et heure</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Élément</th>
                <th className="px-4 py-3">Utilisateur</th>
                {isSuperAdmin && <th className="px-4 py-3">Organisation</th>}
                <th className="px-4 py-3 text-right">Détails</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {logs.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="p-0">
                    {/* Un journal filtré à vide et un journal vide ne se disent pas pareil. */}
                    <EmptyState
                      variant={hasActiveFilters ? 'filtered' : 'empty'}
                      icon={FileClock}
                      title={
                        hasActiveFilters
                          ? 'Aucun événement sur ces critères'
                          : 'Le journal est encore vide'
                      }
                      description={
                        hasActiveFilters
                          ? 'Rien n’a été supprimé : les filtres actifs restreignent la période, l’action ou l’utilisateur recherché.'
                          : 'Ce journal garde la trace de qui a modifié quoi et quand : création d’un camion, changement de prix, suppression d’une facture. Il se remplit tout seul, dès la première action de votre équipe.'
                      }
                      actionLabel={
                        hasActiveFilters ? 'Effacer les filtres' : undefined
                      }
                      onAction={resetFilters}
                      className="border-0"
                    />
                  </td>
                </tr>
              )}
              {logs.map((log) => (
                <tr key={log.id || log._id || log.timestamp || log.changed_by_id} className="hover:bg-muted/50">
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-muted-foreground">
                    <Clock className="mr-1 inline h-3 w-3" />
                    {fmtDate(log.timestamp)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge color={actionColor(log.action)}>{libelle('auditAction', log.action)}</Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <span className="font-medium">{libelle('auditEntity', log.entity_type)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => log.changed_by_id && ouvrirActivite(log.changed_by_id, log.changed_by_name)}
                      className="group inline-flex items-center gap-2 text-left hover:text-flame-ink"
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-info/10">
                        <UserIcon className="h-3 w-3 text-info" />
                      </div>
                      <span className="text-sm font-medium text-muted-foreground group-hover:text-flame-ink">
                        {nomLisible(log.changed_by_name, 'Système')}
                      </span>
                    </button>
                  </td>
                  {isSuperAdmin && (
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {nomLisible(log.organization_name, '—')}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setSelectedLog(log)}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted"
                    >
                      <Eye className="h-3.5 w-3.5" /> Détail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {total > 0 ? <BarrePagination pagination={pagination} nom="actions" className="mt-0" /> : null}

      {/* Détail d'une action */}
      {selectedLog && (
        <DetailAction
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
          onVoirUtilisateur={(id, nom) => {
            setSelectedLog(null);
            ouvrirActivite(id, nom);
          }}
        />
      )}

      {fenetreActivite}
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function AuditLogsPage({ vue = 'securite' }) {
  return vue === 'journal' ? <VueJournal /> : <VueSecurite />;
}

export const AuditSecuritePage = () => <AuditLogsPage vue="securite" />;
export const JournalActionsPage = () => <AuditLogsPage vue="journal" />;
